<template>
    <div class="wab-card">
        <div class="wab-head">
            <div class="wab-head-left">
                <div class="wab-head-icon">
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                    </svg>
                </div>
                <span class="wab-title">Wallet Address Book</span>
            </div>
            <button class="wab-add-btn" @click="openAddFlow" :disabled="wallets.length >= 10">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
                Add Wallet
            </button>
        </div>

        <!-- Empty state -->
        <div v-if="wallets.length === 0 && !showAddFlow" class="wab-empty">
            <div class="wab-empty-icon">
                <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
            </div>
            <p class="wab-empty-title">No wallets connected</p>
            <p class="wab-empty-hint">Connect a BSC wallet via WalletConnect or MetaMask.</p>
            <button class="wab-connect-first-btn" @click="openAddFlow">Connect Wallet</button>
        </div>

        <!-- Wallet list -->
        <div v-if="wallets.length > 0" class="wab-list">
            <div
                v-for="w in wallets"
                :key="w.id"
                class="wab-item"
                :class="{ 'wab-item--primary': w.is_primary }"
            >
                <div class="wab-item-left">
                    <div class="wab-chain-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="color:#F0B90B">
                            <path d="M12 2L7.5 6.5 9.5 8.5 12 6 14.5 8.5 16.5 6.5zM6 10.5L4 12.5 6 14.5 8 12.5zM9.5 15.5L12 18 14.5 15.5 12 13zM18 10.5L16 12.5 18 14.5 20 12.5zM12 10L10 12l2 2 2-2z"/>
                        </svg>
                    </div>
                    <div class="wab-item-info">
                        <div class="wab-label-row">
                            <template v-if="editingId !== w.id">
                                <span class="wab-item-label">{{ w.label }}</span>
                                <button class="wab-edit-btn" @click="startEdit(w)" title="Edit label">
                                    <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                    </svg>
                                </button>
                            </template>
                            <template v-else>
                                <input
                                    ref="labelInputRef"
                                    v-model="editLabel"
                                    maxlength="50"
                                    class="wab-label-input"
                                    @keyup.enter="saveLabel(w)"
                                    @keyup.escape="editingId = null"
                                />
                                <button class="wab-save-label-btn" @click="saveLabel(w)" :disabled="savingLabel">
                                    {{ savingLabel ? '…' : 'Save' }}
                                </button>
                                <button class="wab-cancel-btn" @click="editingId = null">Cancel</button>
                            </template>
                            <span v-if="w.is_primary" class="wab-primary-badge">Primary</span>
                        </div>
                        <div class="wab-item-addr">{{ w.truncated }}</div>
                        <div class="wab-item-meta">
                            <span class="wab-via">{{ formatVia(w.connected_via) }}</span>
                            <span class="wab-dot">·</span>
                            <span class="wab-chain">BSC BEP-20</span>
                        </div>
                    </div>
                </div>

                <div class="wab-item-actions">
                    <button class="wab-action-btn" @click="copyAddress(w.address)" title="Copy address">
                        <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                        </svg>
                    </button>
                    <button
                        v-if="!w.is_primary"
                        class="wab-action-btn wab-action-btn--star"
                        @click="setPrimary(w)"
                        :disabled="settingPrimary"
                        title="Set as primary"
                    >
                        <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                        </svg>
                    </button>
                    <button
                        class="wab-action-btn wab-action-btn--del"
                        @click="removeWallet(w)"
                        :disabled="w.is_primary || deleting === w.id"
                        :title="w.is_primary ? 'Primary wallet cannot be removed' : 'Remove wallet'"
                    >
                        <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Primary lock notice -->
        <div v-if="wallets.length > 0" class="wab-notice">
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20" style="color:#a78bfa;flex-shrink:0;margin-top:2px">
                <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
            </svg>
            <span>Your <strong>Primary</strong> wallet is protected and cannot be removed. Set another wallet as primary first.</span>
        </div>

        <!-- ── Add Wallet Flow ── -->
        <div v-if="showAddFlow" class="wab-flow">
            <div class="wab-flow-head">
                <span class="wab-flow-title">Connect New Wallet</span>
                <button class="wab-flow-close" @click="closeAddFlow">
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>
            </div>

            <!-- label + sign step -->
            <div v-if="flowStep === 'label'" class="wab-flow-body">
                <div class="wab-detected-addr">
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color:#34d399;flex-shrink:0">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span>Detected: <strong class="wab-mono">{{ detectedAddress.substring(0,10) }}…{{ detectedAddress.slice(-8) }}</strong></span>
                </div>
                <div class="wab-flow-field">
                    <label class="wab-flow-lbl">Wallet Label <span style="font-weight:400;opacity:0.5">(optional)</span></label>
                    <input v-model="newLabel" maxlength="50" class="wab-flow-input" placeholder="e.g. My MetaMask" />
                </div>
                <button class="wab-flow-submit" @click="signAndSave" :disabled="flowLoading">
                    <svg v-if="flowLoading" class="wab-spin" width="16" height="16" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    {{ flowLoading ? flowStatus : 'Sign & Save Wallet' }}
                </button>
                <button class="wab-flow-back" @click="closeAddFlow">← Cancel</button>
            </div>

            <!-- WalletConnect opening -->
            <div v-if="flowStep === 'wc'" class="wab-flow-body">
                <div class="wab-wc-info">
                    <svg class="wab-spin" width="22" height="22" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    <span>Opening WalletConnect modal…</span>
                </div>
                <button class="wab-flow-back" @click="closeAddFlow">Cancel</button>
            </div>

            <div v-if="flowError" class="wab-flow-err">{{ flowError }}</div>
        </div>
    </div>
</template>

<script setup>
import { ref, nextTick } from 'vue'
import { BrowserProvider } from 'ethers'
import { getAppKit, openWcModal } from '@/utils/appkit.js'
import { useToast } from '@/composables/useToast.js'

const props = defineProps({
    initialWallets: { type: Array, default: () => [] }
})

const { showToast } = useToast()

// ── State ──────────────────────────────────────────────────────────────────
const wallets         = ref([...props.initialWallets])
const showAddFlow     = ref(false)
const flowStep        = ref('choose')
const flowLoading     = ref(false)
const flowStatus      = ref('')
const flowError       = ref('')
const detectedAddress = ref('')
const newLabel        = ref('')
const detectedVia     = ref('walletconnect')
const activeProvider  = ref(null)

const editingId      = ref(null)
const editLabel      = ref('')
const savingLabel    = ref(false)
const labelInputRef  = ref(null)
const settingPrimary = ref(false)
const deleting       = ref(null)

// ── BSC network helpers ────────────────────────────────────────────────────
const BSC_CHAIN_ID_HEX = '0x38'
const BSC_CHAIN_PARAMS = {
    chainId:           BSC_CHAIN_ID_HEX,
    chainName:         'BNB Smart Chain',
    nativeCurrency:    { name: 'BNB', symbol: 'BNB', decimals: 18 },
    rpcUrls:           ['https://bsc-dataseed.binance.org'],
    blockExplorerUrls: ['https://bscscan.com'],
}

async function ensureBsc(provider) {
    try {
        await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: BSC_CHAIN_ID_HEX }] })
    } catch (err) {
        if (err.code === 4902) {
            await provider.request({ method: 'wallet_addEthereumChain', params: [BSC_CHAIN_PARAMS] })
        } else throw err
    }
}

// ── Add flow ───────────────────────────────────────────────────────────────
async function openAddFlow() {
    showAddFlow.value     = true
    flowStep.value        = 'wc'
    flowError.value       = ''
    detectedAddress.value = ''
    newLabel.value        = ''
    activeProvider.value  = null
    flowLoading.value     = false
    flowStatus.value      = ''

    // ── Fast path: MetaMask / injected extension ─────────────────────────
    // Try eth_accounts first (no popup). If the site is already approved, we
    // get the address immediately and can skip the WalletConnect modal entirely.
    if (typeof window.ethereum !== 'undefined') {
        try {
            const injected = window.ethereum.providers?.find?.(p => p.isMetaMask)
                ?? window.ethereum
            const accounts = await injected.request({ method: 'eth_accounts' }).catch(() => [])

            if (accounts?.length) {
                const addr = accounts[0].toLowerCase()

                if (wallets.value.some(w => w.address.toLowerCase() === addr)) {
                    flowError.value = 'This MetaMask account is already saved. To add a different wallet, switch accounts in MetaMask first, then click Add Wallet again.'
                    return
                }

                // Not yet saved — use MetaMask directly
                try {
                    await ensureBsc(injected)
                } catch (netErr) {
                    if (netErr.code === 4001) {
                        flowError.value = 'Please switch to BNB Smart Chain in MetaMask, then try again.'
                        return
                    }
                    // Other switch error: BSC may already be active — proceed
                }

                detectedAddress.value = addr
                detectedVia.value     = 'metamask'
                activeProvider.value  = injected
                flowStep.value        = 'label'
                return
            }
        } catch {
            // Extension unavailable or error — fall through to WalletConnect
        }
    }

    // ── Fallback: WalletConnect modal ────────────────────────────────────
    connectWalletConnect()
}

function closeAddFlow() {
    showAddFlow.value = false
    flowStep.value    = 'wc'
    flowLoading.value = false
    flowError.value   = ''
    activeProvider.value = null
}

// ── Connect: WalletConnect modal (fallback when no injected extension) ─────
async function connectWalletConnect() {
    flowError.value = ''
    flowStep.value  = 'wc'

    try {
        try { const m = getAppKit(); if (m?.disconnect) await m.disconnect() } catch {}

        const { provider, address } = await openWcModal()

        // Check for duplicate account before network switching
        const addr = address.toLowerCase()
        const isDuplicate = wallets.value.some(w => w.address.toLowerCase() === addr)
        if (isDuplicate) {
            const existing = wallets.value.find(w => w.address.toLowerCase() === addr)
            flowError.value = `This WalletConnect account is already linked as "${existing?.label}". To add a different account, switch wallets in your wallet app, then click Add Wallet again.`
            flowStep.value = 'wc'
            return
        }

        await ensureBsc(provider)

        detectedAddress.value = addr
        detectedVia.value     = 'walletconnect'
        activeProvider.value  = provider
        flowStep.value        = 'label'

    } catch (err) {
        const msg = err.message || ''
        
        // Provide more specific error messages
        if (msg.includes('already linked') || msg.includes('already saved')) {
            flowError.value = 'This account is already linked. Switch accounts in your wallet and try again.'
        } else if (msg.includes('cancelled') || msg.includes('rejected')) {
            flowError.value = 'Connection cancelled. Please try again.'
        } else if (msg.includes('provider not available')) {
            flowError.value = 'Wallet connected but provider unavailable. Please try again.'
        } else if (msg.includes('network') || msg.includes('chain')) {
            flowError.value = 'Network error. Please ensure your wallet is on BNB Smart Chain and try again.'
        } else {
            flowError.value = msg || 'Failed to connect wallet. Please try again.'
        }
        flowStep.value = 'wc'
    }
}

// ── Sign message & save ────────────────────────────────────────────────────
async function signAndSave() {
    if (!detectedAddress.value || !activeProvider.value) return

    flowLoading.value = true
    flowError.value   = ''

    try {
        // 1. Get nonce
        flowStatus.value = 'Requesting nonce…'
        const nonceResp = await fetch('/user/settings/wallets/nonce', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken() },
            body:    JSON.stringify({ address: detectedAddress.value }),
        })
        const nonceData = await nonceResp.json()
        if (!nonceResp.ok) { flowError.value = nonceData.message || 'Failed to get nonce.'; return }

        // 2. Sign via ethers BrowserProvider (works for both MetaMask & WalletConnect)
        flowStatus.value = 'Sign the message in your wallet…'
        let signature
        try {
            const ethersProvider = new BrowserProvider(activeProvider.value)
            const signer         = await ethersProvider.getSigner(detectedAddress.value)
            signature            = await signer.signMessage(nonceData.message)
        } catch (signErr) {
            if (signErr.code === 4001 || signErr.code === 'ACTION_REJECTED') {
                flowError.value = 'Signing rejected by user.'
            } else {
                flowError.value = signErr.message || 'Signing failed.'
            }
            return
        }

        // 3. Save
        flowStatus.value = 'Verifying & saving…'
        const saveResp = await fetch('/user/settings/wallets', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken() },
            body:    JSON.stringify({
                address:       detectedAddress.value,
                signature,
                label:         newLabel.value.trim() || 'My Wallet',
                connected_via: detectedVia.value,
            }),
        })
        const saveData = await saveResp.json()
        if (!saveResp.ok) { flowError.value = saveData.message || 'Failed to save wallet.'; return }

        // 4. Update list
        const existing = wallets.value.findIndex(w => w.id === saveData.wallet.id)
        if (existing >= 0) {
            wallets.value[existing] = saveData.wallet
        } else {
            wallets.value.push(saveData.wallet)
        }
        wallets.value.sort((a, b) => b.is_primary - a.is_primary)

        showToast('Wallet connected successfully!', 'success')
        closeAddFlow()

    } finally {
        flowLoading.value = false
    }
}

// ── Actions ────────────────────────────────────────────────────────────────
async function setPrimary(wallet) {
    settingPrimary.value = true
    try {
        const resp = await fetch(`/user/settings/wallets/${wallet.id}/primary`, {
            method: 'PATCH', headers: { 'X-CSRF-TOKEN': csrfToken() },
        })
        const data = await resp.json()
        if (!resp.ok) { showToast(data.message || 'Failed to update primary.', 'error'); return }

        wallets.value.forEach(w => { w.is_primary = (w.id === wallet.id) })
        wallets.value.sort((a, b) => b.is_primary - a.is_primary)
        showToast('Primary wallet updated.', 'success')
    } catch { showToast('Network error.', 'error') }
    finally { settingPrimary.value = false }
}

async function removeWallet(wallet) {
    if (wallet.is_primary) return
    if (!confirm(`Remove "${wallet.label}" (${wallet.truncated})?`)) return

    deleting.value = wallet.id
    try {
        const resp = await fetch(`/user/settings/wallets/${wallet.id}`, {
            method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrfToken() },
        })
        const data = await resp.json()
        if (!resp.ok) { showToast(data.message || 'Cannot remove this wallet.', 'error'); return }

        wallets.value = wallets.value.filter(w => w.id !== wallet.id)
        showToast('Wallet removed.', 'success')
    } catch { showToast('Network error.', 'error') }
    finally { deleting.value = null }
}

function startEdit(wallet) {
    editingId.value = wallet.id
    editLabel.value = wallet.label
    nextTick(() => {
        const el = Array.isArray(labelInputRef.value) ? labelInputRef.value[0] : labelInputRef.value
        el?.focus()
    })
}

async function saveLabel(wallet) {
    if (!editLabel.value.trim()) return
    savingLabel.value = true
    try {
        const resp = await fetch(`/user/settings/wallets/${wallet.id}/label`, {
            method:  'PATCH',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken() },
            body:    JSON.stringify({ label: editLabel.value.trim() }),
        })
        const data = await resp.json()
        if (!resp.ok) { showToast(data.message || 'Failed to update label.', 'error'); return }

        const w = wallets.value.find(w => w.id === wallet.id)
        if (w) w.label = editLabel.value.trim()
        editingId.value = null
    } catch { showToast('Network error.', 'error') }
    finally { savingLabel.value = false }
}

async function copyAddress(address) {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(address)
        } else {
            const el = document.createElement('textarea')
            el.value = address; el.style.position = 'fixed'; el.style.left = '-99999px'
            document.body.appendChild(el); el.select(); document.execCommand('copy'); el.remove()
        }
        showToast('Address copied!', 'success')
    } catch { showToast('Failed to copy.', 'error') }
}

function formatVia(via) {
    return { walletconnect: 'WalletConnect', metamask: 'MetaMask', trustwallet: 'Trust Wallet', okx: 'OKX', binance: 'Binance' }[via] || via
}

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}
</script>

<style scoped>
.wab-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.13);
    border-radius: 16px; padding: 16px;
    position: relative; overflow: hidden; transition: border-color 0.25s;
}
.wab-card::before {
    content: ''; position: absolute; top: 0; left: 14%; right: 14%; height: 1px;
    background: linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.wab-card:hover { border-color: rgba(167,93,237,0.24); }

.wab-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; flex-wrap: wrap; gap: 8px; }
.wab-head-left { display: flex; align-items: center; gap: 8px; }
.wab-head-icon {
    width: 30px; height: 30px; border-radius: 8px;
    background: rgba(167,93,237,0.12); border: 1px solid rgba(167,93,237,0.22);
    display: flex; align-items: center; justify-content: center; color: #a75ded;
}
.wab-title { font-size: 13px; font-weight: 800; color: #fff; letter-spacing: -0.01em; }
.wab-add-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 6px 12px; border-radius: 8px;
    background: rgba(167,93,237,0.14); border: 1px solid rgba(167,93,237,0.3);
    color: #c084fc; font-size: 11px; font-weight: 700; cursor: pointer; transition: background 0.2s;
}
.wab-add-btn:hover:not(:disabled) { background: rgba(167,93,237,0.22); }
.wab-add-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.wab-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 28px 16px; text-align: center; }
.wab-empty-icon {
    width: 56px; height: 56px; border-radius: 14px;
    background: rgba(167,93,237,0.08); border: 1px solid rgba(167,93,237,0.18);
    display: flex; align-items: center; justify-content: center; color: rgba(167,93,237,0.5);
}
.wab-empty-title { font-size: 14px; font-weight: 700; color: #fff; }
.wab-empty-hint { font-size: 12px; color: rgba(255,255,255,0.45); max-width: 280px; }
.wab-connect-first-btn {
    margin-top: 4px; padding: 8px 18px; border-radius: 9px;
    background: linear-gradient(135deg,#a75ded,#7c3aed);
    border: none; color: #fff; font-size: 12px; font-weight: 700; cursor: pointer;
}
.wab-connect-first-btn:hover { opacity: 0.88; }

.wab-list { display: flex; flex-direction: column; gap: 6px; }
.wab-item {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 12px; border-radius: 10px; gap: 8px;
    background: rgba(10,4,22,0.5); border: 1px solid rgba(167,93,237,0.1); transition: border-color 0.2s;
}
.wab-item--primary { border-color: rgba(167,93,237,0.3); background: rgba(167,93,237,0.05); }
.wab-item:hover { border-color: rgba(167,93,237,0.22); }

.wab-item-left { display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; }
.wab-chain-icon {
    width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
    background: rgba(240,185,11,0.1); border: 1px solid rgba(240,185,11,0.2);
    display: flex; align-items: center; justify-content: center;
}
.wab-item-info { flex: 1; min-width: 0; }
.wab-label-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.wab-item-label { font-size: 12px; font-weight: 700; color: #e2e8f0; }
.wab-primary-badge {
    padding: 1px 7px; border-radius: 20px; font-size: 9px; font-weight: 800;
    text-transform: uppercase; letter-spacing: 0.06em;
    background: rgba(167,93,237,0.18); color: #c084fc; border: 1px solid rgba(167,93,237,0.3);
}
.wab-edit-btn { background: none; border: none; cursor: pointer; padding: 2px 4px; color: rgba(255,255,255,0.3); transition: color 0.15s; }
.wab-edit-btn:hover { color: #c084fc; }
.wab-label-input {
    padding: 3px 8px; border-radius: 6px; font-size: 12px; font-weight: 600;
    background: rgba(10,4,22,0.8); border: 1px solid rgba(167,93,237,0.4);
    color: #fff; outline: none; max-width: 140px;
}
.wab-save-label-btn {
    padding: 2px 8px; border-radius: 5px; font-size: 10px; font-weight: 700;
    background: rgba(167,93,237,0.2); border: 1px solid rgba(167,93,237,0.4); color: #c084fc; cursor: pointer;
}
.wab-cancel-btn {
    padding: 2px 8px; border-radius: 5px; font-size: 10px; font-weight: 700;
    background: transparent; border: 1px solid rgba(255,255,255,0.1); color: rgba(255,255,255,0.4); cursor: pointer;
}
.wab-item-addr { font-family: 'Courier New', monospace; font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 2px; }
.wab-item-meta { display: flex; align-items: center; gap: 4px; margin-top: 1px; }
.wab-via, .wab-chain { font-size: 9px; font-weight: 600; color: rgba(255,255,255,0.3); }
.wab-dot { color: rgba(255,255,255,0.2); }

.wab-item-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.wab-action-btn {
    width: 28px; height: 28px; border-radius: 7px;
    background: rgba(167,93,237,0.06); border: 1px solid rgba(167,93,237,0.15);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: rgba(255,255,255,0.5); transition: background 0.2s, color 0.2s;
}
.wab-action-btn:hover:not(:disabled) { background: rgba(167,93,237,0.14); color: #c084fc; }
.wab-action-btn--star:hover:not(:disabled) { background: rgba(251,191,36,0.12); color: #fbbf24; }
.wab-action-btn--del { color: rgba(239,68,68,0.5); border-color: rgba(239,68,68,0.12); }
.wab-action-btn--del:hover:not(:disabled) { background: rgba(239,68,68,0.1); color: #f87171; }
.wab-action-btn:disabled { opacity: 0.3; cursor: not-allowed; }

.wab-notice {
    display: flex; align-items: flex-start; gap: 7px;
    margin-top: 10px; padding: 8px 10px; border-radius: 8px;
    background: rgba(167,93,237,0.05); border: 1px solid rgba(167,93,237,0.12);
    font-size: 10px; color: rgba(255,255,255,0.45); line-height: 1.5;
}
.wab-notice strong { color: rgba(255,255,255,0.65); }

.wab-flow {
    margin-top: 12px; background: rgba(167,93,237,0.04);
    border: 1px solid rgba(167,93,237,0.2); border-radius: 12px; padding: 14px;
}
.wab-flow-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.wab-flow-title { font-size: 12px; font-weight: 800; color: #c084fc; }
.wab-flow-close { background: none; border: none; cursor: pointer; color: rgba(255,255,255,0.35); transition: color 0.2s; }
.wab-flow-close:hover { color: #fff; }

.wab-flow-body { display: flex; flex-direction: column; gap: 10px; }
.wab-flow-hint { font-size: 11px; color: rgba(255,255,255,0.45); }


.wab-detected-addr {
    display: flex; align-items: center; gap: 6px; padding: 8px 10px; border-radius: 8px;
    background: rgba(52,211,153,0.06); border: 1px solid rgba(52,211,153,0.18);
    font-size: 11px; color: rgba(255,255,255,0.7);
}
.wab-mono { font-family: 'Courier New', monospace; color: #34d399; }

.wab-flow-field { display: flex; flex-direction: column; gap: 5px; }
.wab-flow-lbl { font-size: 10px; font-weight: 700; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.06em; }
.wab-flow-input {
    padding: 8px 12px; border-radius: 8px;
    background: rgba(10,4,22,0.8); border: 1px solid rgba(167,93,237,0.22);
    color: #fff; font-size: 13px; outline: none; transition: border-color 0.2s;
}
.wab-flow-input:focus { border-color: rgba(167,93,237,0.5); }
.wab-flow-submit {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    padding: 9px 16px; border-radius: 9px;
    background: linear-gradient(135deg,#a75ded,#7c3aed);
    border: none; color: #fff; font-size: 12px; font-weight: 700; cursor: pointer;
}
.wab-flow-submit:hover:not(:disabled) { opacity: 0.88; }
.wab-flow-submit:disabled { opacity: 0.5; cursor: not-allowed; }
.wab-flow-back { background: none; border: none; font-size: 11px; color: rgba(255,255,255,0.35); cursor: pointer; padding: 0; }
.wab-flow-back:hover { color: rgba(255,255,255,0.65); }
.wab-flow-err {
    margin-top: 8px; padding: 8px 10px; border-radius: 8px;
    background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2);
    font-size: 11px; color: #f87171;
}
.wab-wc-info { display: flex; align-items: center; gap: 8px; padding: 14px 12px; font-size: 12px; color: rgba(255,255,255,0.6); }
.wab-spin { animation: wab-spin 0.8s linear infinite; }
@keyframes wab-spin { to { transform: rotate(360deg); } }
</style>
